import React, { useState, useEffect, useRef } from 'react';
import { Building, MapPin, Calendar, TrendingUp } from 'lucide-react';

const ProjectsPage = ({ content, language }) => {
  const [projectsVisible, setProjectsVisible] = useState(false);
  const [thesisVisible, setThesisVisible] = useState(false);
  const projectsRef = useRef(null);
  const thesisRef = useRef(null);

  useEffect(() => {
    const isInViewport = (element, offset = 100) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return rect.top <= (window.innerHeight - offset) && rect.bottom >= offset;
    };
    
    const handleScroll = () => {
      if (projectsRef.current && isInViewport(projectsRef.current)) {
        setProjectsVisible(true);
      }
      if (thesisRef.current && isInViewport(thesisRef.current)) {
        setThesisVisible(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16">
      <div 
        ref={projectsRef}
        className="max-w-6xl mx-auto px-6 md:px-12"
      >
        <h1 className={`text-5xl font-bold mb-8 text-center transition-all duration-700 ${projectsVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          {content[language].projects.title}
        </h1>
        <div className={`h-1 w-24 bg-white mx-auto mb-8 transition-all duration-700 delay-200 ${projectsVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></div>
        <p className={`text-xl text-gray-300 mb-16 text-center transition-all duration-700 delay-300 ${projectsVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          {content[language].projects.subtitle}
        </p>
        
        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {content[language].projects.list.map((project, index) => (
            <div 
              key={index}
              className={`bg-gray-900 bg-opacity-30 rounded-lg overflow-hidden transition-all duration-700 hover:bg-opacity-50 ${projectsVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${400 + index * 100}ms` }}
            >
              {/* Project Image Placeholder */}
              <div className="aspect-video bg-gray-800 flex items-center justify-center relative">
                <Building className="w-12 h-12 text-gray-400" />
                <div className="absolute top-4 right-4">
                  <span className={`text-xs px-3 py-1 rounded-full ${
                    project.status === 'Development' || project.status === 'Desarrollo' ? 'bg-green-600' :
                    project.status === 'Planning' || project.status === 'Planificación' ? 'bg-yellow-600' :
                    project.status === 'Innovation' || project.status === 'Innovación' ? 'bg-blue-600' :
                    'bg-orange-600'
                  }`}>
                    {project.status}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-medium">{project.title}</h3>
                </div>
                <p className="text-gray-400 mb-6 leading-relaxed">{project.description}</p>
                
                {/* Project Details */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Location varies by project' : 'Ubicación varía por proyecto'}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Timeline: 2025-2027' : 'Cronograma: 2025-2027'}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Impact-focused development' : 'Desarrollo enfocado en impacto'}
                  </div>
                </div>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="text-xs px-3 py-1 bg-gray-800 rounded-full border border-gray-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Development Thesis Section */}
      <div 
        ref={thesisRef}
        className="bg-gray-900 py-20"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <h2 className={`text-4xl font-bold mb-8 text-center transition-all duration-700 ${thesisVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            {language === 'en' ? 'Our Development Thesis' : 'Nuestra Tesis de Desarrollo'}
          </h2>
          <div className={`h-1 w-24 bg-white mx-auto mb-12 transition-all duration-700 delay-200 ${thesisVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className={`text-center transition-all duration-700 delay-300 ${thesisVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">
                {language === 'en' ? 'Strategic Locations' : 'Ubicaciones Estratégicas'}
              </h3>
              <p className="text-gray-300">
                {language === 'en'
                  ? 'Targeting high-growth gateway cities and emerging markets with strong demographic fundamentals and infrastructure development.'
                  : 'Enfocándose en ciudades de entrada de alto crecimiento y mercados emergentes con fundamentos demográficos sólidos y desarrollo de infraestructura.'}
              </p>
            </div>
            
            <div className={`text-center transition-all duration-700 delay-400 ${thesisVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">
                {language === 'en' ? 'Sustainable Design' : 'Diseño Sostenible'}
              </h3>
              <p className="text-gray-300">
                {language === 'en'
                  ? 'Integrating green technologies, energy efficiency, and sustainable materials to create environmentally responsible developments.'
                  : 'Integrando tecnologías verdes, eficiencia energética y materiales sostenibles para crear desarrollos ambientalmente responsables.'}
              </p>
            </div>
            
            <div className={`text-center transition-all duration-700 delay-500 ${thesisVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4">
                {language === 'en' ? 'Community Impact' : 'Impacto Comunitario'}
              </h3>
              <p className="text-gray-300">
                {language === 'en'
                  ? 'Creating developments that provide affordable housing, local job opportunities, and contribute to neighborhood revitalization.'
                  : 'Creando desarrollos que provean vivienda asequible, oportunidades laborales locales y contribuyan a la revitalización del vecindario.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 text-center">
        <h3 className="text-3xl font-bold mb-6">
          {language === 'en' ? 'Partner With Us' : 'Asóciate Con Nosotros'}
        </h3>
        <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
          {language === 'en'
            ? 'Join us in building the future of sustainable development across the Americas. Whether you\'re an investor, developer, or community leader, we\'d love to explore opportunities together.'
            : 'Únete a nosotros en la construcción del futuro del desarrollo sostenible en las Américas. Ya seas inversionista, desarrollador o líder comunitario, nos encantaría explorar oportunidades juntos.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button className="px-8 py-3 bg-white text-black font-medium rounded hover:bg-gray-200 transition-colors">
            {language === 'en' ? 'Investment Opportunities' : 'Oportunidades de Inversión'}
          </button>
          <button className="px-8 py-3 border border-white text-white font-medium rounded hover:bg-white hover:text-black transition-colors">
            {language === 'en' ? 'Development Partnerships' : 'Alianzas de Desarrollo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;